import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, from, map, catchError, of, forkJoin, switchMap, throwError } from 'rxjs';
import { Jsona } from 'jsona';
import { MOCK_ACADEMIC_OFFICERS } from '../data/mock-academic-officers';
import { AuthService } from './auth.service';
import { ImpersonationContextService } from './impersonation-context.service';
import { ENV } from '../../environments/environment';


/**
 * Best available human-readable message from an error body, across every shape
 * this API produces:
 *   {"errors": {"detail": "..."}}      DRF-JSON:API wrapping a plain Response
 *   {"errors": {"error": "..."}}       workflow endpoints (views._handle_exc)
 *   {"errors": [{"detail": "..."}]}    genuine JSON:API error objects
 *   {"detail": "..."} / {"error": "..."}   unwrapped
 *   {"signed_letter_url": ["..."]}     DRF field validation
 */
export function extractErrorDetail(body: any): string | null {
  if (!body) return null;
  if (typeof body === 'string') return body.trim() || null;

  const errors = body.errors;
  const entry = Array.isArray(errors) ? errors[0] : errors;
  if (entry) {
    if (typeof entry === 'string' && entry.trim()) return entry;
    const nested = entry.detail ?? entry.error ?? firstFieldMessage(entry);
    if (nested) return nested;
  }
  return body.detail ?? body.error ?? firstFieldMessage(body);
}

/**
 * Pull a message out of a DRF field-validation body, e.g.
 *   {"signed_letter_url": ["A signed letter URL is required."]}
 * Returns null when the body is not that shape.
 */
function firstFieldMessage(body: any): string | null {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  for (const value of Object.values(body)) {
    if (typeof value === 'string' && value.trim()) return value;
    if (Array.isArray(value) && typeof value[0] === 'string' && value[0].trim()) return value[0];
  }
  return null;
}

/**
 * One row of the `leave_type` reference table.
 *
 * `description` is the significant field: the API stores it as
 * `RequestedLeave.leave_type_code`, and a create/update is identified by the
 * (`code`, `description`) pair. `id` is unique and is what the form's dropdown
 * binds to; the pair is not unique on its own.
 */
export interface LeaveTypeOption {
  readonly id: number;
  readonly code: string;
  readonly description: string;
}

/** Who made a change. Null when the change came from the data feed. */
export interface HistoryUser {
  readonly id: number;
  readonly username: string;
  readonly full_name: string | null;
}

/** One field that differed from the previous version of the same row. */
export interface HistoryChange {
  readonly field: string;
  readonly old: string | number | boolean | null;
  readonly new: string | number | boolean | null;
}

/**
 * One tracked edit to an academic officer's record.
 *
 * A **diff against the previous version of that row**, not a snapshot — so
 * `changes` is empty for `created` and `deleted`, which have nothing to compare
 * against. The API also drops updates whose only differences are bookkeeping
 * fields, so every entry here represents a real change.
 */
export interface HistoryEntry {
  /** Registry slug, e.g. 'leave-request', 'tenured-service-record'. */
  readonly type: string;
  readonly object_id: number;
  readonly history_date: string;
  readonly history_user: HistoryUser | null;
  readonly history_type: 'created' | 'updated' | 'deleted';
  readonly changes: readonly HistoryChange[];
}

/** One action in a leave request's approval timeline. */
export interface ApprovalHistoryEntry {
  readonly id: number;
  /** DEPARTMENT | SCHOOL | ACADEMIC_APPOINTMENTS | VICE_PROVOST */
  readonly approval_level: string;
  /** APPROVED | RECYCLED | REJECTED | CANCELED */
  readonly approval_status: string;
  readonly approver_name: string | null;
  readonly approver_uni: string | null;
  readonly comments: string | null;
  /** When the action was taken. */
  readonly approved_at: string | null;
  readonly created: string | null;
}

@Injectable({ providedIn: 'root' })
export class JsonaService {
  private baseUrl = ENV.apiBaseUrl;
  private academicOfficerUrl = `${this.baseUrl}academic-officers/`;
  private academicOfficerUrl2 = `${this.baseUrl}academic-officer/`;
  private jsona = new Jsona();
  private authService = inject(AuthService);
  private impersonationContext = inject(ImpersonationContextService);
  
  // Flag to enable/disable mock data - defaults to false, user must explicitly enable
  private includeMockData = false;

  constructor(private http: HttpClient) {}

  // Centralized error handling method
  private handleApiError = (error: HttpErrorResponse): Observable<never> => {
    console.error('API Error:', error);

    // Normalise the error payload. DRF-JSON:API wraps whatever the view returned
    // under `errors`, which is an OBJECT for our plain Response({...}) views
    // ({"errors": {"detail": "..."}}) and an ARRAY for genuine JSON:API errors.
    // Treating it as always-array meant errorsArray[0] was undefined and every
    // backend message was replaced by Angular's generic transport error.
    const rawErrors = error.error?.errors;
    const errorsArray = Array.isArray(rawErrors)
      ? rawErrors
      : (rawErrors && typeof rawErrors === 'object' ? [rawErrors] : []);

    // Extract error code: JSON:API errors[0].code > legacy error_type > infer from status
    const errorCode = errorsArray[0]?.code
      || error.error?.error_type
      || this.inferErrorType(error, errorsArray);

    // Extract detail message. Order matters — the workflow endpoints return
    // {"error": "..."} (see leave_request/views._handle_exc) and DRF field
    // validation returns {"field": ["message"]}. Neither is JSON:API shaped, so
    // without these the caller fell through to Angular's generic
    // "Http failure response for ..." and the actionable message was lost.
    const errorDetail = extractErrorDetail(error.error) || error.message;

    // Create structured error object matching the expected format
    const structuredError = {
      status: error.status,
      message: errorDetail,
      error: {
        error_type: errorCode,
        errors: errorsArray,
        detail: errorDetail
      },
      originalError: error
    };

    return throwError(() => structuredError);
  };

  /**
   * Error handler for endpoints declaring `responseType: 'blob'`.
   *
   * On a failure Angular hands back the body as an unparsed Blob, so the normal
   * extraction sees an opaque object, finds no `errors`/`detail`, and falls back
   * to Angular's generic "Http failure response for ..." — losing an actionable
   * backend message such as *"No letter template mapped for
   * leave_type_code='per', salary_status='' ..."*. Re-read the Blob as text and
   * parse it, then hand the real body to the usual handler.
   */
  private handleBlobApiError = (error: HttpErrorResponse): Observable<never> => {
    if (!(error.error instanceof Blob)) return this.handleApiError(error);
    return from(error.error.text()).pipe(
      switchMap((text) => {
        let body: unknown;
        try {
          body = JSON.parse(text);
        } catch {
          // Not JSON — an S3 XML fault or an HTML gateway page. Surfacing that
          // raw would put a wall of markup in front of the user, so keep the
          // original error and let the caller's own wording stand.
          return this.handleApiError(error);
        }
        return this.handleApiError(new HttpErrorResponse({
          error: body,
          status: error.status,
          statusText: error.statusText,
          url: error.url ?? undefined,
        }));
      }),
    );
  };

  // Infer error type from status code when not provided by API
  private inferErrorType(error: HttpErrorResponse, errorsArray: any[] = []): string {
    // Check JSON:API detail for token-related messages
    const detail = errorsArray[0]?.detail || error.error?.detail || '';

    switch (error.status) {
      case 401:
        if (detail.toLowerCase().includes('token') || detail.toLowerCase().includes('expired')) {
          return 'invalid_token';
        }
        return 'no_user_found';
      case 402:
        return 'insufficient_perms';
      case 403:
        return 'invalid_token';
      case 404:
        return 'resource_not_found';
      default:
        return 'unknown_error';
    }
  }

  private getHeaders(includeImpersonation = true): HttpHeaders {
    let headers = new HttpHeaders();

    // Always use OAuth bearer token for API authentication
    const oauthToken = this.authService.getAccessToken();
    if (oauthToken) {
      headers = headers.set('Authorization', `Bearer ${oauthToken}`);
    }

    // While impersonating, tell the backend whose view to reproduce (officer,
    // reporting-unit/department scope, or viewer group). Reads are narrowed and
    // writes rejected server-side. Token/identity stays the real admin.
    //
    // Callers that must query as the REAL user (e.g. the Impersonate page's own
    // officer/RU/department search, so you can switch targets without ending the
    // current impersonation first) pass includeImpersonation=false.
    if (includeImpersonation) {
      const impHeaders = this.impersonationContext.headersFor();
      for (const [k, v] of Object.entries(impHeaders)) {
        headers = headers.set(k, v);
      }
    }

    return headers;
  }

  // Public method to control mock data inclusion
  setMockDataEnabled(enabled: boolean): void {
    this.includeMockData = enabled;
  }

  isMockDataEnabled(): boolean {
    return this.includeMockData;
  }

  getAcademicOfficer(empId: string): Observable<any> {
    // If ID starts with "10" (mock IDs) and mock data is enabled, check mock first
    if (this.includeMockData && empId.startsWith('10')) {
      return this.getMockAcademicOfficerById(empId).pipe(
        catchError(error => {
          console.warn(`Mock officer ${empId} not found, trying API:`, error);
          return this.getApiAcademicOfficer(empId);
        })
      );
    }

    // Otherwise try API first, fallback to mock if enabled
    return this.getApiAcademicOfficer(empId).pipe(
      catchError(error => {
        if (this.includeMockData) {
          console.warn(`API lookup failed for empId ${empId}, trying mock data:`, error);
          return this.getMockAcademicOfficerById(empId);
        }
        throw error;
      })
    );
  }

  private getApiAcademicOfficer(empId: string): Observable<any> {
    // Deliberately no `include` param.
    //
    // AcademicOfficerDetailSerializer.JSONAPIMeta.included_resources already
    // declares every relationship the app needs — campus, positions, service
    // records, leave and sabbatical history, extensions, subsequent reviews.
    // Sending `include` does not add to that default, it REPLACES it: passing
    // "positions,admin_department,reporting_unit" narrowed the response to
    // those three and silently dropped the rest, which blanked the Campus field
    // and the service record sections. Leave this off unless you intend to
    // restrict the payload, and then list every path the caller needs.
    return this.http.get<any>(`${this.academicOfficerUrl}${empId}/`, {
      headers: this.getHeaders(),
    }).pipe(
      map((data) => {
        const officer = this.jsona.deserialize(data);
        this.normalizeOfficerServiceRecords(officer);
        return officer;
      }),
      catchError(this.handleApiError)
    );
  }

  // The API embeds service records as nested JSON:API objects within the officer's attributes.
  // JSONA only flattens the top-level resource, leaving nested objects with their own
  // `attributes` wrapper intact. This step promotes those attributes to the top level so the
  // rest of the app can access fields like `record.tenure_clock` directly.
  private normalizeOfficerServiceRecords(officer: any): void {
    const flattenIfNeeded = (r: any) =>
      r?.attributes ? { id: r.id, type: r.type, ...r.attributes } : r;

    for (const key of ['tenure_track_service_records', 'nontenured_service_records', 'subsequent_reviews']) {
      if (Array.isArray(officer[key])) {
        officer[key] = officer[key].map(flattenIfNeeded);
      }
    }
    for (const key of ['tenure_service_record', 'nontenured_service_record']) {
      if (officer[key]) officer[key] = flattenIfNeeded(officer[key]);
    }
  }

  private getMockAcademicOfficerById(id: string): Observable<any> {
    return of(MOCK_ACADEMIC_OFFICERS).pipe(
      map(mockData => {
        // Clone the data to make it mutable for Jsona
        const clonedData = JSON.parse(JSON.stringify(mockData));
        const officers = this.jsona.deserialize(clonedData);
        const officersArray = Array.isArray(officers) ? officers : [officers];
        const officer = officersArray.find(o => o.id === id);
        
        if (!officer) {
          throw new Error(`Mock officer with ID ${id} not found`);
        }
        
        return officer;
      })
    );
  }

  /**
   * Search Academic Officers.
   *
   * `status` is an optional comma-separated list of employment-status buckets
   * (`active` | `inactive` | `retiree`, or `all`). Omitting it keeps the backend
   * default of active-only, so basic search behaviour is unchanged; Advanced
   * Search passes it to reach inactive/retired officers.
   *
   * `aoType` is one of the six derived officer categories (see deriveAoType in
   * advanced-search.models.ts). Omitting it applies no category restriction.
   *
   * `options.impersonatable` drops officers who cannot sign in to CAPS
   * (inactive/retired, or visiting up-or-out — caps-backend `ao_access.py`),
   * for the View As picker.
   *
   * Filtering happens server-side (caps-backend `parse_ao_types` / `ao_type_q`
   * in views.py) because the totals in `meta.pagination` must reflect the
   * filtered set — narrowing a page after it arrives would report "20 results"
   * while showing three.
   *
   * The slugs are a shared contract with the backend, which derives the same
   * category from tenure_type plus the job_function prefix of the full-time
   * position. Both sides must agree with deriveAoType() in
   * advanced-search.models.ts, or a filtered search returns rows whose visible
   * AO Type contradicts the filter that produced them.
   */
  searchAcademicOfficer(
    query: string,
    pageNumber: number = 1,
    pageSize: number = 100,
    bypassImpersonation = false,
    status?: string,
    aoType?: string,
    options: { impersonatable?: boolean } = {},
  ): Observable<any> {
    const params = new URLSearchParams();
    params.set('search', query);
    params.set('page[number]', pageNumber.toString());
    params.set('page[size]', pageSize.toString());
    if (status) params.set('status', status);
    if (aoType) params.set('ao_type', aoType);
    if (options.impersonatable) params.set('impersonatable', 'true');

    // const observables: Observable<any>[] = [];

    // // Always include mock data if flag is enabled - COMMENTED OUT FOR NOW
    // if (this.includeMockData) {
    //   const mockResults$ = this.getMockAcademicOfficers(query).pipe(
    //     map(mockOfficers => ({
    //       data: mockOfficers.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    //       meta: {
    //         total_records: mockOfficers.length,
    //         total_pages: Math.ceil(mockOfficers.length / pageSize),
    //         current_page: pageNumber,
    //         page_size: pageSize
    //       }
    //     }))
    //   );
    //   observables.push(mockResults$);
    // }
    
    // API call with JSON API pagination
    const apiResults$ = this.http.get<any>(`${this.academicOfficerUrl}all/?${params.toString()}`, {headers: this.getHeaders(!bypassImpersonation)}).pipe(
      map((data) => {
        const deserialized = this.jsona.deserialize(data);
        const officers = Array.isArray(deserialized) ? deserialized : [deserialized];

        // The endpoint paginates JSON:API style, so the true total lives at
        // meta.pagination.count. It is normalised to total_records here because
        // that is what callers read, and because the fallback below cannot tell
        // the truth: with no meta, the only "total" available is the number of
        // rows in hand — which is the page size, not the result count. That
        // fallback previously made the pager believe there was a single page.
        const pagination = data?.meta?.pagination;
        if (pagination) {
          return {
            data: officers,
            meta: {
              total_records: pagination.count,
              total_pages: pagination.pages,
              current_page: pagination.page,
              page_size: pageSize,
            },
          };
        }

        // Older shape, kept for any caller still served by an unpaginated route.
        if (data?.meta) {
          return { data: officers, meta: data.meta };
        }

        return {
          data: officers,
          meta: {
            total_records: officers.length,
            total_pages: 1,
            current_page: 1,
            page_size: officers.length,
          },
        };
      }),
      catchError(error => {
        console.warn('API search failed:', error);
        // For search operations, we might want to handle errors differently - return empty results instead of throwing
        if (error.status === 401 || error.status === 402 || error.status === 403) {
          return this.handleApiError(error);
        }
        return of({
          data: [],
          meta: {
            total_records: 0,
            total_pages: 0,
            current_page: pageNumber,
            page_size: pageSize
          }
        });
      })
    );
    
    // Return API results directly (mock data combination commented out for now)
    return apiResults$;
    
    // // COMMENTED OUT - Combine mock and API results logic
    // const observables: Observable<any>[] = [];
    // observables.push(apiResults$);
    // if (this.includeMockData && observables.length > 1) {
    //   return forkJoin(observables).pipe(
    //     map(([mockResult, apiResult]) => {
    //       const combined = [...mockResult.data, ...apiResult.data];
    //       const seen = new Set();
    //       const uniqueOfficers = combined.filter(officer => {
    //         const key = officer.id || officer.uni || officer.emp_id;
    //         if (seen.has(key)) {
    //           return false;
    //         }
    //         seen.add(key);
    //         return true;
    //       });
    //       
    //       return {
    //         data: uniqueOfficers.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    //         meta: {
    //           total_records: uniqueOfficers.length,
    //           total_pages: Math.ceil(uniqueOfficers.length / pageSize),
    //           current_page: pageNumber,
    //           page_size: pageSize
    //         }
    //       };
    //     })
    //   );
    // } else {
    //   return apiResults$;
    // }
  }

  private getMockAcademicOfficers(query: string): Observable<any[]> {
    return of(MOCK_ACADEMIC_OFFICERS).pipe(
      map(mockData => {
        // Clone the data to make it mutable for Jsona
        const clonedData = JSON.parse(JSON.stringify(mockData));
        const officers = this.jsona.deserialize(clonedData);
        const officersArray = Array.isArray(officers) ? officers : [officers];
        
        const searchTerm = query.toLowerCase().trim();
        return officersArray.filter(officer => 
          officer.first_name?.toLowerCase().includes(searchTerm) ||
          officer.last_name?.toLowerCase().includes(searchTerm) ||
          officer.uni?.toLowerCase().includes(searchTerm) ||
          officer.emp_id?.toLowerCase().includes(searchTerm) ||
          officer.email?.toLowerCase().includes(searchTerm) ||
          (officer.position_department?.description || officer.position_department?.name || officer.position_department || '').toLowerCase().includes(searchTerm) ||
          officer.title?.toLowerCase().includes(searchTerm)
        );
      })
    );
  }

  createLeaveRequest(payload: object): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}leave-requests/`, payload, {
      headers: this.getHeaders().set('Content-Type', 'application/vnd.api+json'),
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  patchLeaveRequest(id: string, payload: object): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}leave-requests/${id}/`, payload, {
      headers: this.getHeaders().set('Content-Type', 'application/vnd.api+json'),
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  /**
   * Upload a file to S3 via the multipart /upload/documents/ endpoint.
   * The `s3Folder` becomes the FormData field name and therefore the S3 prefix.
   * Returns the S3 URL of the uploaded file.
   */
  uploadLeaveRequestFile(file: File, s3Folder: string): Observable<string> {
    const fd = new FormData();
    fd.append(s3Folder, file);
    return this.http.post<any>(`${this.baseUrl}upload/documents/`, fd, {
      headers: this.getHeaders().delete('Content-Type'),
    }).pipe(
      map(res => (res.uploadedFiles?.[0]?.s3Url ?? '') as string),
      catchError(this.handleApiError)
    );
  }

  /**
   * Upload a file to S3 via the base64 /upload/documents/base64/ endpoint,
   * preserving `fixedFileName` as the S3 object key (enabling overwrites on re-upload).
   * Used for medical approval documents where idempotent S3 keys are required.
   * Returns the S3 URL.
   */
  uploadLeaveRequestFileFixed(file: File, s3Folder: string, fixedFileName: string): Observable<string> {
    return new Observable<string>(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        const base64Content = dataUrl.substring(dataUrl.indexOf(',') + 1);
        const body = {
          data: {
            type: 'upload_documents_base64',
            attributes: {
              files: [{ fieldName: s3Folder, fileName: fixedFileName, content: base64Content }],
            },
          },
        };
        this.http.post<any>(`${this.baseUrl}upload/documents/base64/`, body, {
          headers: this.getHeaders().set('Content-Type', 'application/vnd.api+json'),
        }).pipe(
          map(res => (res.uploadedFiles?.[0]?.s3Url ?? '') as string),
          catchError(this.handleApiError)
        ).subscribe({
          next: url => { observer.next(url); observer.complete(); },
          error: err => observer.error(err),
        });
      };
      reader.onerror = () => observer.error(new Error('Failed to read file for upload'));
      reader.readAsDataURL(file);
    });
  }

  getLeaveRequest(id: string): Observable<any> {
    const params = new HttpParams().set(
      'include',
      'academic_officer,academic_officer.positions,academic_officer.admin_department,academic_officer.reporting_unit'
    );
    return this.http.get<any>(`${this.baseUrl}leave-requests/${id}/`, {
      headers: this.getHeaders(), params
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  getLeaveRequests(filters: Record<string, string> = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => { params = params.set(k, v); });
    params = params.set(
      'include',
      'academic_officer,academic_officer.positions,academic_officer.admin_department,academic_officer.reporting_unit'
    );
    return this.http.get<any>(`${this.baseUrl}leave-requests/`, {
      headers: this.getHeaders(), params
    }).pipe(
      map((data) => {
        const result = this.jsona.deserialize(data);
        return Array.isArray(result) ? result : (result ? [result] : []);
      }),
      catchError(this.handleApiError)
    );
  }

  /**
   * Leave requests awaiting the caller's review, rather than the ones they
   * submitted. Optionally narrowed to specific review queues by the `key`
   * values from `getLeaveReviewBreakdown()`; omit (or pass every key) for the
   * full queue. Scoping is enforced server-side — unknown keys yield nothing.
   */
  getLeaveReviewQueue(scopeKeys?: string[]): Observable<any[]> {
    let params = new HttpParams().set('review', '1');
    for (const key of scopeKeys ?? []) {
      params = params.append('review_scope', key);
    }
    params = params.set(
      'include',
      'academic_officer,academic_officer.positions,academic_officer.admin_department,academic_officer.reporting_unit'
    );
    return this.http.get<any>(`${this.baseUrl}leave-requests/`, {
      headers: this.getHeaders(), params
    }).pipe(
      map((data) => {
        const result = this.jsona.deserialize(data);
        return Array.isArray(result) ? result : (result ? [result] : []);
      }),
      catchError(this.handleApiError)
    );
  }

  abandonLeaveRequest(id: string | number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}leave-requests/${id}/abandon/`, {},
      { headers: this.getHeaders() }
    ).pipe(catchError(this.handleApiError));
  }

  /**
   * Full chronological approval / recycle / rejection timeline for a request.
   *
   * Every action is its own row server-side — the service always creates, never
   * updates — so repeated recycles at the same level all appear. Read-only.
   *
   * The endpoint answers {leave_request_id, history: [...]}, which the API's
   * envelope then wraps in {data: ...}; unwrap both shapes.
   */
  getLeaveRequestApprovalHistory(id: string | number): Observable<ApprovalHistoryEntry[]> {
    return this.http.get<any>(
      `${this.baseUrl}leave-requests/${id}/approval-history/`,
      { headers: this.getHeaders() }
    ).pipe(
      map((resp) => {
        const body = resp?.data ?? resp;
        return (body?.history ?? []) as ApprovalHistoryEntry[];
      }),
      catchError(this.handleApiError)
    );
  }

  departmentReviewLeaveRequest(id: string | number, body: {
    approved: boolean; comments?: string; recycle?: boolean;
    medical_approval_url?: string | null; outside_dept_approval_urls?: string[];
  }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/department-review/`, body,
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  schoolReviewLeaveRequest(id: string | number, body: {
    approved: boolean; comments?: string; recycle?: boolean;
    medical_approval_url?: string | null; outside_dept_approval_urls?: string[];
  }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/school-review/`, body,
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  /**
   * Generate the approval letter and return the URL to download it from.
   *
   * The API answers {"data": {"url": "<presigned S3 URL>"}}. The letter is
   * rendered from current data on every call and cannot be edited, so the URL
   * is the deliverable — reviewers download and read it, and only the Vice
   * Provost uploads anything back (the signed copy).
   *
   * The URL is presigned and expires, so it must be used promptly and is not
   * worth storing.
   */
  generateLeaveLetter(id: string | number): Observable<string> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/generate-letter/`, {},
      { headers: this.getHeaders() }
    ).pipe(
      map((resp) => {
        const url = (resp?.data ?? resp)?.url;
        if (typeof url !== 'string' || !url) {
          throw new Error('The letter was generated but no download link was returned.');
        }
        return url;
      }),
      catchError(this.handleApiError)
    );
  }

  /**
   * Ask the API for a temporary link to a stored document.
   *
   * Documents live in a private S3 bucket (ACLs disabled, no public grant), so
   * the stored object URL is useless to the browser — it answers AccessDenied —
   * and the API deliberately never returns one. Instead the server authorizes
   * the caller and mints a presigned link that expires in minutes.
   *
   * Resolve one per click and use it immediately: it is a bearer credential for
   * that one file, so it must not be cached, stored, or logged.
   *
   * `disposition: 'inline'` is for rendering in the leave-request preview
   * dialog; the default downloads the file under the name the user originally
   * uploaded.
   */
  private downloadUrlFrom(endpoint: string, disposition: 'attachment' | 'inline'): Observable<string> {
    const query = disposition === 'inline' ? '?disposition=inline' : '';
    return this.http.get<any>(`${endpoint}${query}`, { headers: this.getHeaders() }).pipe(
      map((resp) => {
        const url = (resp?.data ?? resp)?.url;
        if (typeof url !== 'string' || !url) {
          throw new Error('The server did not return a download link for this document.');
        }
        return url;
      }),
      catchError(this.handleApiError)
    );
  }

  /** Temporary link to one leave-request attachment. See downloadUrlFrom. */
  getLeaveAttachmentDownloadUrl(
    id: string | number,
    attachmentId: string | number,
    disposition: 'attachment' | 'inline' = 'attachment',
  ): Observable<string> {
    return this.downloadUrlFrom(
      `${this.baseUrl}leave-requests/${id}/attachments/${attachmentId}/download-url/`,
      disposition,
    );
  }

  /**
   * Temporary link to one academic officer profile document. See downloadUrlFrom.
   *
   * Always an attachment: profile documents are downloaded, never previewed in
   * the page. Only leave-request attachments render inline.
   */
  getOfficerDocumentDownloadUrl(
    empId: string,
    documentId: string | number,
  ): Observable<string> {
    return this.downloadUrlFrom(
      `${this.academicOfficerUrl2}${empId}/documents/${documentId}/download-url/`,
      'attachment',
    );
  }

  aaReviewLeaveRequest(id: string | number, body: {
    approved: boolean; comments?: string; recycle?: boolean;
  }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/aa-review/`, body,
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  /**
   * No signed_letter_url: the VP uploads the signed letter as an attachment
   * before approving, and the API resolves it from there. Sending one meant the
   * client needed a raw S3 URL to hand back, which it no longer receives.
   */
  vpReviewLeaveRequest(id: string | number, body: {
    approved: boolean; comments?: string; recycle?: boolean;
    additional_notification_unis?: string[];
  }): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/vp-review/`, body,
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  cancelLeaveRequest(id: string | number, cancel_comment?: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/cancel/`, { cancel_comment: cancel_comment ?? '' },
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  resubmitLeaveRequest(id: string | number): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}leave-requests/${id}/resubmit/`, {},
      { headers: this.getHeaders().set('Content-Type', 'application/json') }
    ).pipe(catchError(this.handleApiError));
  }

  getAcademicOfficerByEmail(email: string): Observable<any> {
    return this.http.get<any>(`${this.academicOfficerUrl}all/?filter[email]=${email}`, {headers: this.getHeaders()}).pipe(
      map((data) => this.jsona.deserialize(data)),
      // Return first result if array, otherwise return the object
      map(result => Array.isArray(result) && result.length > 0 ? result[0] : result),
      catchError(this.handleApiError)
    );
  }

  getAcademicOfficerNotes(empId: string): Observable<any[]> {
    return this.http.get<any>(`${this.academicOfficerUrl}${empId}/notes/`, {headers: this.getHeaders()}).pipe(
      map((data) => this.jsona.deserialize(data)),
      // Ensure we always return an array
      map(result => Array.isArray(result) ? result : [result]),
      catchError(this.handleApiError)
    );
  }

  createAcademicOfficerNote(empId: string, noteText: string, type: 'public' | 'work' = 'public'): Observable<any> {
    const payload = new URLSearchParams();
    payload.set('type', type);
    payload.set('note_text', noteText);
    
    const headers = this.getHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    
    return this.http.post<any>(`${this.academicOfficerUrl}${empId}/notes/`, payload.toString(), { headers }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  getAcademicOfficerWorkNotes(empId: string): Observable<any[]> {
    return this.http.get<any>(`${this.academicOfficerUrl}${empId}/work-notes/`, {headers: this.getHeaders()}).pipe(
      map((data) => this.jsona.deserialize(data)),
      // Ensure we always return an array
      map(result => Array.isArray(result) ? result : [result]),
      catchError(this.handleApiError)
    );
  }

  createAcademicOfficerWorkNote(empId: string, noteText: string): Observable<any> {
    const payload = new URLSearchParams();
    payload.set('type', 'work');
    payload.set('work_note_text', noteText);
    
    const headers = this.getHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
    
    return this.http.post<any>(`${this.academicOfficerUrl}${empId}/work-notes/`, payload.toString(), { headers }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  updateAcademicOfficer(empId: string, updateData: any): Observable<any> {
    const headers = this.getHeaders().set('Content-Type', 'application/json');
    
    return this.http.patch<any>(`${this.academicOfficerUrl}${empId}/`, updateData, { headers }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  /**
   * Fetch the officer's ID photo as a binary blob.
   * Returns the raw image (JPEG/PNG) from the access-control reporting server.
   */
  getAcademicOfficerPhoto(empId: string): Observable<Blob> {
    return this.http.get(`${this.academicOfficerUrl}${empId}/photo/`, {
      headers: this.getHeaders(),
      responseType: 'blob',
    });
  }


  /**
   * The Documents section of an academic officer's profile.
   *
   * This endpoint does NOT return JSON:API resource objects — it returns plain
   * rows inside the renderer's `data` envelope, with no `type` and no
   * `attributes`. It must therefore not go through `jsona.deserialize`.
   *
   * That was not merely redundant. JSONA keys its identity map on type + id,
   * and with no type every row collapses onto its id alone. Once signed leave
   * letters joined the list the two sources could hand back the same id — a
   * profile document #1 and a leave attachment #1 — and JSONA merged them into
   * one object, dropping `file_name` and crashing the icon lookup on render.
   *
   * Rows are returned as they arrive. Callers key on source + id, never id.
   */
  getAcademicOfficerDocuments(empId: string, page: number = 1, pageSize: number = 10): Observable<any> {
    return this.http.get<any>(`${this.academicOfficerUrl2}${empId}/documents/`, {headers: this.getHeaders()}).pipe(
      map((resp) => {
        const rows = resp?.data ?? resp;
        return Array.isArray(rows) ? rows : [];
      }),
      catchError(this.handleApiError)
    );
  }

  // Step 1: Upload file to S3 via upload service
  uploadDocumentToS3(file: File, category: string = 'profile'): Observable<any> {
    const formData = new FormData();
    formData.append(category, file);
    
    return this.http.post<any>(`${this.baseUrl}upload/documents/`, formData, {
      headers: this.getHeaders().delete('Content-Type') // Let browser set content-type for multipart
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Step 2: Associate uploaded document with officer
  associateDocumentWithOfficer(empId: string, fileName: string, s3Url: string): Observable<any> {
    const payload = {
      data: {
        type: "upload_document",
        attributes: {
          document_name: fileName,
          document_url: s3Url,
          emp_id: empId
        }
      }
    };
    console.log('associateDocumentWithOfficer doc name', fileName);

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.post<any>(`${this.academicOfficerUrl2}${empId}/documents/`, payload, { headers }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  // Complete two-step upload process
  uploadOfficerDocument(empId: string, file: File, category: string = 'profile'): Observable<any> {
    return this.uploadDocumentToS3(file, category).pipe(
      switchMap((uploadResponse) => {
        // Extract first uploaded file info
        const uploadedFile = uploadResponse.uploadedFiles[0];
        if (!uploadedFile) {
          throw new Error('No file was uploaded');
        }

        // Step 2: Associate with officer
        return this.associateDocumentWithOfficer(
          empId,
          uploadedFile.originalFileName,
          uploadedFile.s3Url
        );
      })
    );
  }

  deleteAcademicOfficerDocument(empId: string, documentId: string): Observable<any> {
    return this.http.delete<any>(`${this.academicOfficerUrl2}${empId}/documents/${documentId}/`, {headers: this.getHeaders()}).pipe(
      catchError(this.handleApiError)
    );
  }

  downloadAcademicOfficerDocument(empId: string, documentId: string): Observable<Blob> {
    return this.http.get(`${this.academicOfficerUrl2}${empId}/documents/${documentId}`, {
      headers: this.getHeaders(),
      responseType: 'blob'
    }).pipe(
      catchError(this.handleBlobApiError)
    );
  }

  // Download file from any URL (like S3) with bearer token authentication
  downloadFromUrl(url: string): Observable<Blob> {
    return this.http.get(url, {
      headers: this.getHeaders(),
      responseType: 'blob'
    }).pipe(
      catchError(this.handleBlobApiError)
    );
  }

  // Get admin dashboard stats
  getStats(filters?: { reporting_unit_id?: number; department_id?: number }): Observable<any> {
    const params = new URLSearchParams();
    if (filters?.reporting_unit_id) params.set('reporting_unit_id', filters.reporting_unit_id.toString());
    if (filters?.department_id)     params.set('department_id',     filters.department_id.toString());
    const query = params.toString();
    return this.http.get<any>(`${this.baseUrl}stats/${query ? '?' + query : ''}`, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleApiError));
  }

  // Get PAC feed worklist with JSON API style pagination
  getPacFeedWorklist(pageNumber: number = 1, pageSize: number = 10): Observable<any> {
    const params = new URLSearchParams();
    params.set('page[number]', pageNumber.toString());
    params.set('page[size]', pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}pac-feed/worklist/?${params.toString()}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  getPacFeedComparison(empId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}pac-feed/${empId}/comparison/`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Approve PAC feed item
  approvePacFeedItem(rptId: string): Observable<any> {
    
    return this.http.patch<any>(`${this.baseUrl}pac-feed/${rptId}/approve/`, {'status': 'APPROVED'}, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Reject PAC feed item
  rejectPacFeedItem(rptId: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}pac-feed/${rptId}/approve/`, {'status': 'REJECTED'}, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Group Management API Methods
  
  // Get all groups
  getGroups(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}groups/`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Assign user to group with optional scope (reporting unit and/or department)
  assignUserToGroup(uni: string, groupId: string, reportingUnitId?: number | null, departmentId?: number | null): Observable<any> {
    const attributes: Record<string, any> = {
      uni: uni,
      group_id: parseInt(groupId)
    };
    if (reportingUnitId != null) {
      attributes['reporting_unit_id'] = reportingUnitId;
    }
    if (departmentId != null) {
      attributes['department_id'] = departmentId;
    }

    const payload = {
      data: {
        type: "user_group_assignment",
        attributes
      }
    };

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.post<any>(`${this.baseUrl}groups/assign/`, payload, {
      headers: headers
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Remove user from group with optional scope (reporting unit and/or department)
  removeUserFromGroup(uni: string, groupId: string, reportingUnitId?: number | null, departmentId?: number | null): Observable<any> {
    const attributes: Record<string, any> = {
      uni: uni,
      group_id: parseInt(groupId)
    };
    if (reportingUnitId != null) {
      attributes['reporting_unit_id'] = reportingUnitId;
    }
    if (departmentId != null) {
      attributes['department_id'] = departmentId;
    }

    const payload = {
      data: {
        type: "user_group_removal",
        attributes
      }
    };

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.post<any>(`${this.baseUrl}groups/remove/`, payload, {
      headers: headers
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Reference Data Methods

  getDepartments(filters?: Record<string, string>, bypassImpersonation = false): Observable<any> {
    let params = new HttpParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => params = params.set(k, v));
    }
    return this.http.get<any>(`${this.baseUrl}departments/`, {
      headers: this.getHeaders(!bypassImpersonation),
      params,
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  /**
   * Fetch departments with Jsona deserialization (resolves included relationships).
   * Returns department objects with `reporting_unit` as a resolved nested object.
   */
  getDepartmentsResolved(filters?: Record<string, string>, bypassImpersonation = false): Observable<any[]> {
    let params = new HttpParams().set('include', 'reporting_unit');
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => params = params.set(k, v));
    }
    return this.http.get<any>(`${this.baseUrl}departments/`, {
      headers: this.getHeaders(!bypassImpersonation),
      params,
    }).pipe(
      map((data) => {
        const deserialized = this.jsona.deserialize(data);
        return Array.isArray(deserialized) ? deserialized : [deserialized];
      }),
      catchError(this.handleApiError)
    );
  }

  /**
   * Audit trail across every tracked model on one academic officer's record.
   *
   * Fetched in one call without `type`: the API merges all ten tracked models
   * and sorts newest-first, so filtering by type client-side costs nothing and
   * avoids ten round trips. `type` is here for a caller that genuinely wants
   * one slice.
   *
   * There is no pagination server-side, and the volume is real — one officer
   * can carry ~100 entries — so callers should load this on demand rather than
   * with the page.
   */
  getAcademicOfficerHistory(empId: string, type?: string): Observable<HistoryEntry[]> {
    let params = new HttpParams().set('academic_officer', empId);
    if (type) params = params.set('type', type);

    return this.http.get<any>(`${this.baseUrl}history/`, {
      headers: this.getHeaders(),
      params,
    }).pipe(
      map((resp) => {
        const entries = (resp?.data ?? resp)?.history;
        return Array.isArray(entries) ? entries as HistoryEntry[] : [];
      }),
      catchError(this.handleApiError)
    );
  }

  /**
   * Reference list of leave types for the leave-request form's dropdown.
   *
   * The API pages at 2000 by default, so the whole table arrives in one call.
   * Rows are returned as stored — including near-duplicates that differ only by
   * id, and descriptions that are bare codes. Callers decide what to show; see
   * `canonicalLeaveTypes` in leave-request-form.ts for the dedup rule.
   */
  getLeaveTypes(): Observable<LeaveTypeOption[]> {
    return this.http.get<any>(`${this.baseUrl}leave-types/`, {
      headers: this.getHeaders(),
    }).pipe(
      map((data) => {
        const deserialized = this.jsona.deserialize(data);
        const rows = Array.isArray(deserialized) ? deserialized : [deserialized];
        return rows
          .filter((r: any) => r && r.id != null)
          .map((r: any) => ({
            id: Number(r.id),
            code: String(r.code ?? ''),
            description: String(r.description ?? '').trim(),
          }))
          // A row with no description has nothing to label it with in a
          // dropdown, and its description is what the API stores as
          // leave_type_code — so it could never round-trip anyway.
          .filter((r: LeaveTypeOption) => !!r.description);
      }),
      catchError(this.handleApiError)
    );
  }

  /**
   * Count of leave requests awaiting review at the given level, scoped to the
   * caller. `level` ∈ department|school|academic_appointments|vice_provost.
   * Optional reportingUnitId/departmentId narrow the scope. Used only when
   * previewing a specific (narrower) target — e.g. admin impersonation of a
   * scoped role. For the caller's own real review count, use
   * `getLeaveReviewBreakdown()` instead, which sums across every role the
   * caller actually holds. Resolves to a number.
   */
  getLeaveReviewCount(
    level: string,
    opts?: { reportingUnitId?: string | number; departmentId?: string | number },
  ): Observable<number> {
    let params = new HttpParams().set('level', level);
    if (opts?.reportingUnitId != null) params = params.set('reporting_unit', String(opts.reportingUnitId));
    if (opts?.departmentId != null) params = params.set('department', String(opts.departmentId));
    return this.http.get<any>(`${this.baseUrl}leave-requests/review-count/`, {
      headers: this.getHeaders(),
      params,
    }).pipe(
      // DRF's JSON:API renderer wraps plain APIView responses in a `data`
      // envelope, so the count lives at resp.data.count — reading resp.count
      // silently yielded undefined (→ 0), which is why this tile always
      // showed 0. Accept both shapes in case the renderer changes.
      map((resp) => (resp?.data ?? resp)?.count ?? 0),
      catchError(this.handleApiError)
    );
  }

  /**
   * Auto-derived review counts across every review role the caller holds
   * (department, school, academic_appointments, vice_provost — whichever
   * their roles grant), summed server-side. `breakdown` gives one entry per
   * (level, scope) — group_name, reporting_unit/department, and count — so
   * the UI can show "requests to review, per role."
   */
  getLeaveReviewBreakdown(): Observable<{
    count: number;
    breakdown: Array<{
      key: string;
      level: string;
      status: string;
      group_name: string | null;
      reporting_unit: number | null;
      reporting_unit_name: string | null;
      department: number | null;
      department_name: string | null;
      count: number;
    }>;
  }> {
    return this.http.get<any>(`${this.baseUrl}leave-requests/review-count/`, {
      headers: this.getHeaders(),
    }).pipe(
      // Unwrap the JSON:API `data` envelope (see getLeaveReviewCount above).
      map((resp) => {
        const body = resp?.data ?? resp;
        return { count: body?.count ?? 0, breakdown: body?.breakdown ?? [] };
      }),
      catchError(this.handleApiError)
    );
  }

  getReportingUnits(search?: string, bypassImpersonation = false): Observable<any> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    return this.http.get<any>(`${this.baseUrl}reporting-units/`, {
      headers: this.getHeaders(!bypassImpersonation),
      params,
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  /**
   * Search reporting units by name/code for the impersonation scope picker.
   * Queries as the REAL user (bypassImpersonation) so an admin can switch from
   * one impersonated role to another without first ending impersonation.
   */
  searchReportingUnits(query: string): Observable<any[]> {
    return this.getReportingUnits(query, /* bypassImpersonation */ true).pipe(
      map((resp) => resp?.data ?? [])
    );
  }

  /**
   * Search departments by name/code for the impersonation scope picker.
   * Queries as the REAL user (see searchReportingUnits).
   */
  searchDepartments(query: string): Observable<any[]> {
    return this.getDepartments(query ? { search: query } : undefined, /* bypassImpersonation */ true).pipe(
      map((resp) => resp?.data ?? [])
    );
  }

  /**
   * Ask the backend to validate an impersonation target before entering it.
   * The candidate target is passed as QUERY PARAMS (not X-Impersonate-* headers)
   * so the request (a) validates against the REAL user's scope — never the
   * currently-active impersonation — and (b) needs no custom-header CORS
   * preflight. `params` keys: officer | scope_type + scope_id | group.
   */
  validateImpersonation(candidate: Record<string, string>): Observable<any> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(candidate)) {
      params = params.set(k, v);
    }
    return this.http.get<any>(`${this.baseUrl}impersonation/validate/`, {
      headers: this.getHeaders(/* includeImpersonation */ false),
      params,
    }).pipe(
      map((resp) => resp?.data?.attributes ?? resp),
      catchError(this.handleApiError)
    );
  }

  /**
   * Fetch academic officers from the /reports/ endpoint with JSON:API filter params.
   * Used by both /academic-officer-search (filtered results) and /report (full results by tenure type).
   */
  getReports(
    pageNumber = 1,
    pageSize = 100,
    filters: Record<string, string> = {}
  ): Observable<{ data: any[]; meta: any }> {
    const params = new URLSearchParams();
    params.set('page[number]', pageNumber.toString());
    params.set('page[size]', pageSize.toString());
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
    return this.http.get<any>(`${this.baseUrl}reports/?${params.toString()}`, { headers: this.getHeaders() })
      .pipe(
        map((data) => {
          const deserialized = this.jsona.deserialize(data);
          const officers = Array.isArray(deserialized) ? deserialized : (deserialized ? [deserialized] : []);
          return {
            data: officers,
            meta: (data as any).meta ?? {
              total_records: officers.length,
              total_pages: 1,
              current_page: pageNumber,
              page_size: officers.length,
            },
          };
        }),
        catchError(error => {
          if ([401, 402, 403].includes(error.status)) return this.handleApiError(error);
          return of({
            data: [],
            meta: { total_records: 0, total_pages: 0, current_page: pageNumber, page_size: pageSize },
          });
        })
      );
  }

  // Current User — uses GET /me/ to fetch the authenticated user's profile and roles.
  // This is the primary endpoint for the logged-in user to get their own data.
  getCurrentUser(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}me/`, {
      headers: this.getHeaders()
    }).pipe(
      map((response) => {
        const raw = response?.data;
        if (!raw) return null;
        const included: any[] = (response as any).included || [];

        // Resolve role_assignment objects from the included array using relationship refs
        const roleRefs: any[] = raw.relationships?.roles?.data || [];
        const roles = roleRefs
          .map((ref: any) => included.find((inc: any) => inc.type === ref.type && inc.id === ref.id))
          .filter(Boolean)
          .map((inc: any) => ({ id: inc.id, ...inc.attributes }));

        return { id: raw.id, type: raw.type, ...raw.attributes, roles };
      }),
      catchError(this.handleApiError)
    );
  }

  // User Management Methods (admin only)

  // Search users by UNI (username) — admin endpoint for managing users.
  // Returns flattened user object with all attributes + resolved roles from included.
  searchUsers(query: string): Observable<any> {
    const params = new URLSearchParams();
    params.set('uni', query);

    return this.http.get<any>(`${this.baseUrl}users/?${params.toString()}`, {
      headers: this.getHeaders()
    }).pipe(
      map((response) => {
        const raw = response?.data;
        if (!raw) return null;
        const included: any[] = (response as any).included || [];

        // Resolve role_assignment objects from the included array using relationship refs
        const resolveRoles = (data: any) => {
          const roleRefs: any[] = data.relationships?.roles?.data || [];
          const roles = roleRefs
            .map((ref: any) => included.find((inc: any) => inc.type === ref.type && inc.id === ref.id))
            .filter(Boolean)
            .map((inc: any) => ({ id: inc.id, ...inc.attributes }));
          return { id: data.id, type: data.type, ...data.attributes, roles };
        };

        // Single JSON:API resource object → flatten + resolve roles
        if (!Array.isArray(raw)) {
          return resolveRoles(raw);
        }
        // Array of resources (future-proofing)
        return raw.map(resolveRoles);
      }),
      catchError(error => {
        console.error('Error searching users:', error);
        if (this.includeMockData) {
          return this.getMockUsersForSearch(query);
        }
        return this.handleApiError(error);
      })
    );
  }

  // Search users by keyword (name, UNI, email) — uses API: GET /users/?uni=xxx
  // Resolves role_assignment objects from the JSON:API included array.
  // page/pageSize are optional — backend doesn't support pagination yet, but params are forwarded when provided
  searchUsersKeyword(query: string, page?: number, pageSize?: number): Observable<{ count: number; results: any[] }> {
    const params = new URLSearchParams();
    params.set('uni', query);
    if (page != null) params.set('page', page.toString());
    if (pageSize != null) params.set('page_size', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}users/?${params.toString()}`, {
      headers: this.getHeaders()
    }).pipe(
      map((response) => {
        const raw = response?.data;
        if (!raw) return { count: 0, results: [] };
        const included: any[] = (response as any).included || [];

        // Resolve role_assignment objects from the included array using relationship refs
        const resolveRoles = (data: any) => {
          const roleRefs: any[] = data.relationships?.roles?.data || [];
          const roles = roleRefs
            .map((ref: any) => included.find((inc: any) => inc.type === ref.type && inc.id === ref.id))
            .filter(Boolean)
            .map((inc: any) => ({ id: inc.id, ...inc.attributes }));
          return { id: data.id, type: data.type, ...data.attributes, roles };
        };

        // Paginated collection: attributes has { count, results: [...] }
        if (!Array.isArray(raw) && Array.isArray(raw.attributes?.results)) {
          return { count: raw.attributes.count ?? raw.attributes.results.length, results: raw.attributes.results };
        }

        // Single JSON:API resource (direct UNI lookup → one user)
        if (!Array.isArray(raw)) {
          return { count: 1, results: [resolveRoles(raw)] };
        }

        // Array of resource objects (future-proofing)
        return { count: raw.length, results: raw.map(resolveRoles) };
      }),
      catchError(error => {
        console.error('Error in keyword search:', error);
        if (this.includeMockData) {
          return this.getMockUsersForSearch(query);
        }
        return this.handleApiError(error);
      })
    );
  }

  // Combined search: queries both User keyword search and AO APIs in parallel
  searchUsersAndOfficers(query: string): Observable<{ users: any; officers: any }> {
    return forkJoin({
      users: this.searchUsersKeyword(query).pipe(
        catchError(() => of({ count: 0, results: [] }))
      ),
      officers: this.searchAcademicOfficer(query, 1, 50).pipe(
        catchError(() => of({ data: [], meta: { total_records: 0 } }))
      )
    });
  }

  // Get user by ID
  getUserById(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}users/${userId}/`, {
      headers: this.getHeaders()
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(error => {
        console.error('Error getting user:', error);
        if (this.includeMockData) {
          return this.getMockUser(userId);
        }
        return this.handleApiError(error);
      })
    );
  }

  // Create new user
  createUser(userData: any): Observable<any> {
    const payload = {
      data: {
        type: "user",
        attributes: userData
      }
    };

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.post<any>(`${this.baseUrl}users/`, payload, {
      headers: headers
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  // Update user
  updateUser(userId: string, userData: any): Observable<any> {
    const payload = {
      data: {
        type: "user",
        id: userId,
        attributes: userData
      }
    };

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.put<any>(`${this.baseUrl}users/${userId}/`, payload, {
      headers: headers
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  /** @deprecated Roles are now embedded in the user detail response (GET /users/?uni=xxx). Use searchUsers() instead. */
  getUserRoles(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}users/${userId}/roles/`, {
      headers: this.getHeaders()
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  /** @deprecated Use assignUserToGroup() with scope params instead. */
  addUserRole(userId: string, roleData: any): Observable<any> {
    const payload = {
      data: {
        type: "user_role",
        attributes: roleData
      }
    };

    const headers = this.getHeaders().set('Content-Type', 'application/vnd.api+json');

    return this.http.post<any>(`${this.baseUrl}users/${userId}/roles/`, payload, {
      headers: headers
    }).pipe(
      map((data) => this.jsona.deserialize(data)),
      catchError(this.handleApiError)
    );
  }

  /** @deprecated Use removeUserFromGroup() with scope params instead. */
  removeUserRole(userId: string, roleId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}users/${userId}/roles/${roleId}/`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(this.handleApiError)
    );
  }

  // Mock data for development
  private getMockUsersForSearch(query: string): Observable<any> {
    const mockUsers = [
      {
        id: '1',
        type: 'user',
        attributes: {
          id: 1,
          username: 'js5470@columbia.edu',
          email: 'js5470@columbia.edu',
          first_name: 'John',
          last_name: 'Smith',
          is_active: true,
          date_joined: '2023-01-15T10:30:00Z',
          employee_id: '0038245',
          department: 'Computer Science',
          school: 'School of Engineering',
          roles: ['Admin', 'Department User']
        }
      },
      {
        id: '2',
        type: 'user',
        attributes: {
          id: 2,
          username: 'jd432@columbia.edu',
          email: 'jd432@columbia.edu',
          first_name: 'Jane',
          last_name: 'Doe',
          is_active: true,
          date_joined: '2022-09-01T08:15:00Z',
          employee_id: '0045123',
          department: 'Chemical Engineering',
          school: 'School of Chemical Engineering',
          roles: ['School User']
        }
      },
      {
        id: '3',
        type: 'user',
        attributes: {
          id: 3,
          username: 'dr789@columbia.edu',
          email: 'dr789@columbia.edu',
          first_name: 'David',
          last_name: 'Rodriguez',
          is_active: true,
          date_joined: '2023-05-20T14:45:00Z',
          employee_id: '0052341',
          department: 'Law',
          school: 'Law School',
          roles: ['Professor']
        }
      }
    ];

    const filteredUsers = mockUsers.filter(user => {
      const searchTerm = query.toLowerCase();
      return user.attributes.username.toLowerCase().includes(searchTerm) ||
             user.attributes.first_name.toLowerCase().includes(searchTerm) ||
             user.attributes.last_name.toLowerCase().includes(searchTerm) ||
             user.attributes.employee_id?.includes(searchTerm);
    });

    return of({ data: filteredUsers });
  }

  private getMockUser(userId: string): Observable<any> {
    const mockUser = {
      id: userId,
      type: 'user',
      attributes: {
        id: parseInt(userId),
        username: 'js5470@columbia.edu',
        email: 'js5470@columbia.edu',
        first_name: 'John',
        last_name: 'Smith',
        is_active: true,
        date_joined: '2023-01-15T10:30:00Z',
        employee_id: '0038245',
        department: 'Computer Science',
        school: 'School of Engineering',
        roles: ['Admin', 'Department User']
      }
    };

    return of({ data: mockUser });
  }
}
